/**
 * `ad_units.copy` ⇄ `ConceptCopyVariant`.
 *
 * ── NO MIGRATION, AND WHY THAT IS SAFE ────────────────────────────────────
 * `ad_units.copy` is a plain `jsonb` column with no CHECK constraint (verified
 * against prod 2026-08-12: the only CHECKs on the table are on `ad_platform`,
 * `offer_class`, `status`, `source` and the review-metadata pairing). The tone
 * and anchor a copy variant needs are therefore stored INSIDE the payload, in
 * a namespaced `conceptCopy` block, rather than in two new columns.
 *
 * That is not a shortcut around the existing readers — it is required by them.
 * `MetaAdUnitCopyReadSchema` / `LinkedInAdUnitCopyReadSchema` in
 * `src/lib/briefs/ad-schemas.ts` are deliberately NOT `.strict()` (their own
 * doc comment explains why: the writer has already changed shape once and the
 * reader must tolerate every shape ever written). Unknown keys are ignored, so
 * `BriefConceptAdUnits` keeps rendering a workbench-authored row as ordinary ad
 * copy, while this module reads the extra block. A row written here is a real
 * `ad_units` row in every existing surface, not a private format wearing the
 * table's name.
 *
 * ── ANCHOR LINEAGE IS NOT `supersedes_id` ─────────────────────────────────
 * `ad_units.supersedes_id` exists and points at a row this one REPLACES — it
 * belongs to the durable revision ledger, where the pointer means the older row
 * is no longer current. "More like this" produces SIBLINGS: the anchor stays
 * live, on screen, and selectable. Writing the anchor into `supersedes_id`
 * would tell every ledger reader the anchor had been retired, which is false.
 */

import type { AdPlatformId } from '@/constants/ad-specs';
import type { CopyField } from '@/lib/concept-copy/platform-limits';
import {
  type ConceptCopyBriefEvidenceLineage,
  type ConceptCopyVariant,
  measureField,
} from '@/lib/concept-copy/types';

/** Bumped only if the block's shape changes; readers below tolerate absence. */
export const CONCEPT_COPY_PAYLOAD_VERSION = 1;

export interface ConceptCopyDraft {
  headline: string;
  caption: string;
  cta: string;
  /** Present only when the model returned one. Never synthesised. */
  tone?: string;
  evidence: string[];
}

export interface ConceptCopyMetaBlock {
  version: number;
  tone?: string;
  anchorAdUnitId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function firstText(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  for (const entry of value) {
    if (isRecord(entry) && typeof entry.text === 'string' && entry.text.trim()) return entry.text;
  }
  return null;
}

/**
 * Serialise one draft into the platform's own persisted copy shape.
 *
 * The workbench writes the SAME field names the rest of the ad-copy pipeline
 * writes, so nothing downstream needs a special case:
 *   • Meta     — `primaryTexts` / `headlines` / `cta`
 *   • LinkedIn — `commentary` / `headline`
 * Google RSA is deliberately absent: an RSA is an asset inventory of up to 15
 * headlines that Google recombines per auction, not a headline/caption/CTA
 * triplet, and pretending otherwise would write a row that reads as a complete
 * RSA while carrying one of each. `generate_ad_copy` remains the tool for that.
 */
export function toPersistedCopy(
  adPlatform: Exclude<AdPlatformId, 'google_rsa'>,
  draft: ConceptCopyDraft,
  anchorAdUnitId?: string | null,
): Record<string, unknown> {
  const meta: ConceptCopyMetaBlock = { version: CONCEPT_COPY_PAYLOAD_VERSION };
  if (draft.tone) meta.tone = draft.tone;
  if (anchorAdUnitId) meta.anchorAdUnitId = anchorAdUnitId;

  if (adPlatform === 'linkedin') {
    return {
      commentary: draft.caption,
      headline: draft.headline,
      // LinkedIn renders its call to action as a provider button, so `cta` is
      // carried as workbench state rather than as a LinkedIn copy field.
      cta: draft.cta,
      evidence: draft.evidence,
      conceptCopy: meta,
    };
  }

  return {
    primaryTexts: [{ text: draft.caption, shape: 'punchy' }],
    headlines: [{ text: draft.headline, role: 'outcome' }],
    description: '',
    cta: draft.cta,
    evidence: draft.evidence,
    conceptCopy: meta,
  };
}

export interface ConceptCopyRow {
  id: string;
  brief_id: string | null;
  project_task_id: string | null;
  ad_platform: string;
  status: string;
  copy: unknown;
  brief_evidence_lineage?: unknown;
  created_at: string;
}

function parseBriefEvidenceLineage(value: unknown): ConceptCopyBriefEvidenceLineage | null {
  if (!isRecord(value)) return null;
  const state = value.state;
  const sourceLinkIds = value.sourceLinkIds;
  const sourceCount = value.sourceCount;
  const limitations = value.limitations;
  const capturedAt = value.capturedAt;
  if (
    (state !== 'linked' && state !== 'no_decision' && state !== 'no_evidence') ||
    !Array.isArray(sourceLinkIds) ||
    !sourceLinkIds.every((id) => typeof id === 'string') ||
    typeof sourceCount !== 'number' ||
    !Number.isInteger(sourceCount) ||
    sourceCount < 0 ||
    !Array.isArray(limitations) ||
    !limitations.every((limitation) => typeof limitation === 'string') ||
    typeof capturedAt !== 'string'
  ) {
    return null;
  }

  const decisionValue = value.decision;
  let decision: ConceptCopyBriefEvidenceLineage['decision'] = null;
  if (decisionValue !== null) {
    if (!isRecord(decisionValue)) return null;
    const channelRole = decisionValue.channelRole;
    if (
      (decisionValue.audience !== null && typeof decisionValue.audience !== 'string') ||
      (decisionValue.offer !== null && typeof decisionValue.offer !== 'string') ||
      (decisionValue.hypothesis !== null && typeof decisionValue.hypothesis !== 'string') ||
      (decisionValue.testVariable !== null && typeof decisionValue.testVariable !== 'string') ||
      (channelRole !== null &&
        channelRole !== 'paid' &&
        channelRole !== 'organic' &&
        channelRole !== 'shared_strategy') ||
      (decisionValue.successMeasure !== null && typeof decisionValue.successMeasure !== 'string') ||
      typeof decisionValue.version !== 'number' ||
      !Number.isInteger(decisionValue.version) ||
      typeof decisionValue.updatedAt !== 'string'
    ) {
      return null;
    }
    decision = {
      audience: decisionValue.audience as string | null,
      offer: decisionValue.offer as string | null,
      hypothesis: decisionValue.hypothesis as string | null,
      testVariable: decisionValue.testVariable as string | null,
      channelRole: channelRole as 'paid' | 'organic' | 'shared_strategy' | null,
      successMeasure: decisionValue.successMeasure as string | null,
      version: decisionValue.version,
      updatedAt: decisionValue.updatedAt,
    };
  }

  if (sourceLinkIds.length !== sourceCount) return null;
  if (state === 'linked' && (decision === null || sourceCount === 0)) return null;
  if (state === 'no_evidence' && (decision === null || sourceCount !== 0)) return null;
  if (state === 'no_decision' && decision !== null) return null;

  return { state, decision, sourceLinkIds, sourceCount, limitations, capturedAt };
}

/**
 * Read one stored row back as a variant.
 *
 * Returns null — never a half-built variant — when the row carries no readable
 * headline or caption. A card rendering an empty string would look like copy
 * the model chose to leave blank.
 */
export function parseVariantRow(row: ConceptCopyRow): ConceptCopyVariant | null {
  if (!isRecord(row.copy) || !row.project_task_id) return null;
  const copy = row.copy;

  const adPlatform = (
    row.ad_platform === 'linkedin' || row.ad_platform === 'google_rsa' ? row.ad_platform : 'meta'
  ) as AdPlatformId;

  const headline =
    typeof copy.headline === 'string' && copy.headline.trim()
      ? copy.headline
      : firstText(copy.headlines);
  // A Google search ad keeps its body text in `descriptions`, not `primaryTexts`; without this the
  // option read as a headline with no caption beside it.
  const caption =
    typeof copy.commentary === 'string' && copy.commentary.trim()
      ? copy.commentary
      : (firstText(copy.primaryTexts) ??
        (adPlatform === 'google_rsa' ? firstText(copy.descriptions) : null));
  const cta = typeof copy.cta === 'string' ? copy.cta : '';

  if (!headline && !caption) return null;

  const block = isRecord(copy.conceptCopy) ? copy.conceptCopy : null;
  const tone = typeof block?.tone === 'string' && block.tone.trim() ? block.tone.trim() : undefined;
  const anchor = typeof block?.anchorAdUnitId === 'string' ? block.anchorAdUnitId : null;

  const fields = ([] as { field: CopyField; text: string }[])
    .concat(headline ? [{ field: 'headline' as const, text: headline }] : [])
    .concat(caption ? [{ field: 'caption' as const, text: caption }] : [])
    .concat(cta.trim() ? [{ field: 'cta' as const, text: cta }] : [])
    .map((entry) => measureField(entry.field, entry.text));

  const variant: ConceptCopyVariant = {
    id: row.id,
    conceptId: row.project_task_id,
    briefId: row.brief_id,
    adPlatform,
    fields,
    anchoredOnVariantId: anchor,
    status: (row.status as ConceptCopyVariant['status']) ?? 'draft',
    createdAt: row.created_at,
  };
  // Assigned conditionally: an absent tone must not become `tone: undefined`,
  // which would still be a key on the object and could be spread onto a card.
  if (tone) variant.tone = tone;
  const briefEvidenceLineage = parseBriefEvidenceLineage(row.brief_evidence_lineage);
  if (briefEvidenceLineage) variant.briefEvidenceLineage = briefEvidenceLineage;
  return variant;
}

export const CONCEPT_COPY_VARIANT_COLUMNS =
  'id, brief_id, project_task_id, ad_platform, status, copy, brief_evidence_lineage, created_at';
